import React, { useState, useEffect, useRef } from "react";
import {
  Upload,
  FileText,
  Globe,
  ShieldCheck,
  Trash2,
  CheckCircle,
  Eye,
  EyeOff,
  Settings,
  Database,
  Layers,
  ChevronRight,
  ChevronLeft,
  ChevronDown,
  Save,
  RefreshCcw,
  Info,
  AlertCircle,
  Brain,
  Hash,
  Download,
  FolderTree,
  Folder
} from "lucide-react";
import ApiClient from "../utils/api";
import { chunkinIcon, rerankinIcon, multimodalIcon, uploadHierarch, folderHierarch } from "../components/icons";
import { useToast } from "../context/ToastContext";
import DeleteConfirmModal from "../components/DeleteConfirmModal";
import PageHeader from "../components/PageHeader";
import Dropdown from "../components/Dropdown";

const DocumentUpload = ({ activeAiAgentId }) => {
  const { showToast } = useToast();
  const [activeTab, setActiveTab] = useState("repository");
  const [documents, setDocuments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [websiteUrl, setWebsiteUrl] = useState("");
  const [scraping, setScraping] = useState(false);
  const [firecrawlApiKey, setFirecrawlApiKey] = useState("");
  const [firecrawlConfigured, setFirecrawlConfigured] = useState(false);
  const [savingFirecrawl, setSavingFirecrawl] = useState(false);
  const [showFirecrawlKey, setShowFirecrawlKey] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [docToDelete, setDocToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const fileInputRef = useRef(null);

  // Chunks Drawer State
  const [isChunksDrawerOpen, setIsChunksDrawerOpen] = useState(false);
  const [selectedDocForChunks, setSelectedDocForChunks] = useState(null);
  const [chunks, setChunks] = useState([]);
  const [loadingChunks, setLoadingChunks] = useState(false);

  // Zip/Folder Repository Upload State
  const [zipRepository, setZipRepository] = useState("");
  const [zipFile, setZipFile] = useState(null);
  const [uploadingZip, setUploadingZip] = useState(false);
  const zipInputRef = useRef(null);

  // Folder Hierarchy Tree State
  const [folderTree, setFolderTree] = useState([]);
  const [repositories, setRepositories] = useState([]);
  const [selectedRepository, setSelectedRepository] = useState("");
  const [loadingFolders, setLoadingFolders] = useState(false);
  const [expandedFolders, setExpandedFolders] = useState({});

  // Pagination State
  const [currentPage, setCurrentPage] = useState(1);
  const DOCS_PER_PAGE = 10;

  // Vector Config State
  const [configSaving, setConfigSaving] = useState(false);
  const [vectorConfig, setVectorConfig] = useState({
    strategy: 'RecursiveChunker',
    chunk_size: 2000,
    chunk_overlap: 200,
    search_top_k: 5,
    search_threshold: 0.5,
    search_type: 'hybrid',
    knowledge_source: 'vector',
    neo4j_uri: 'bolt://localhost:7687',
    neo4j_user: 'neo4j',
    neo4j_password: '',
    neo4j_database: 'neo4j',
    params: {}
  });

  const strategies = [
    {
      id: 'RecursiveChunker',
      name: 'Recursive Splitting',
      icon: Layers,
      description: 'Splits text by natural boundaries like paragraphs and periods. Recommended for most documents.',
      params: ['chunk_size', 'chunk_overlap']
    },
    {
      id: 'MarkdownChunker',
      name: 'Markdown Aware',
      icon: FileText,
      description: 'Splits based on headers (#, ##). Best for documentation and structured MD files.',
      params: ['chunk_size', 'chunk_overlap', 'split_on_headings']
    },
    {
      id: 'SemanticChunker',
      name: 'Semantic AI',
      icon: Brain,
      description: 'Uses AI to group sentences with similar meanings. Highly accurate but slower.',
      params: ['chunk_size', 'similarity_threshold', 'similarity_window']
    },
    {
      id: 'AgenticChunker',
      name: 'Agentic (LLM)',
      icon: Settings,
      description: 'Asks an LLM to find the best breakpoints. Extremely context-aware.',
      params: ['chunk_size']
    },
    {
      id: 'FixedSizeChunker',
      name: 'Fixed Size',
      icon: Hash,
      description: 'Hard cut at exact character count. Fastest but may break sentences.',
      params: ['chunk_size', 'chunk_overlap']
    }
  ];

  const documentPollTimerRef = useRef(null);

  useEffect(() => {
    loadDocuments();
    loadFirecrawlConfig();
    fetchVectorConfig();
    return () => {
      if (documentPollTimerRef.current) {
        clearTimeout(documentPollTimerRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeAiAgentId]);

  useEffect(() => {
    if (activeTab === "folders") {
      loadRepositories();
      loadFolderTree();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, activeAiAgentId]);

  useEffect(() => {
    if (activeTab === "folders") {
      loadFolderTree();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedRepository]);

  const loadDocuments = async () => {
    if (documentPollTimerRef.current) {
      clearTimeout(documentPollTimerRef.current);
    }
    try {
      const response = await ApiClient.getDocuments();
      const docs = response.documents || [];
      const formattedDocs = docs.map(doc => ({
        ...doc,
        name: doc.original_name,
        dbId: doc.id
      }));
      setDocuments(formattedDocs);
      setCurrentPage(1); // Reset to first page on load

      const hasProcessing = formattedDocs.some(doc => doc.status === "processing");
      if (hasProcessing) {
        documentPollTimerRef.current = setTimeout(loadDocuments, 5000);
      }
    } catch (error) {
      console.error("Failed to load documents:", error);
    }
  };

  const loadFirecrawlConfig = async () => {
    try {
      const response = await ApiClient.request("/firecrawl/config");
      if (response.configured) {
        setFirecrawlConfigured(true);
        setFirecrawlApiKey(response.api_key || "");
      }
    } catch (error) {
      console.error("Failed to load Firecrawl config:", error);
      setFirecrawlConfigured(false);
    }
  };

  const fetchVectorConfig = async () => {
    try {
      const response = await ApiClient.request('/api/vector/config');
      if (response) {
        setVectorConfig({
          ...response,
          params: response.params || {}
        });
      }
    } catch (error) {
      console.error('Error fetching vector config:', error);
    }
  };

  const saveVectorConfig = async () => {
    setConfigSaving(true);
    try {
      await ApiClient.request('/api/vector/config', {
        method: 'POST',
        body: JSON.stringify(vectorConfig)
      });
      showToast("Vectorization parameters synchronized successfully!", "success");
    } catch (error) {
      console.error('Error saving vector config:', error);
      showToast("Failed to save neural parameters", "error");
    } finally {
      setConfigSaving(false);
    }
  };

  const saveFirecrawlConfig = async () => {
    if (!firecrawlApiKey.trim()) {
      showToast("Please enter a Firecrawl API key", "warning");
      return;
    }
    setSavingFirecrawl(true);
    try {
      await ApiClient.request("/firecrawl/config", {
        method: "POST",
        body: JSON.stringify({ api_key: firecrawlApiKey }),
      });
      showToast("Firecrawl API key saved successfully!", "success");
      setFirecrawlConfigured(true);
      await loadFirecrawlConfig();
    } catch (error) {
      console.error("Failed to save Firecrawl config:", error);
      showToast(`Failed to save Firecrawl API key: ${error.message}`, "error");
    } finally {
      setSavingFirecrawl(false);
    }
  };

  const loadRepositories = async () => {
    try {
      const response = await ApiClient.getRepositories();
      setRepositories(response.repositories || []);
    } catch (error) {
      console.error("Failed to load repositories:", error);
    }
  };

  const loadFolderTree = async () => {
    setLoadingFolders(true);
    try {
      const response = await ApiClient.getFolderDocuments(selectedRepository || null);
      setFolderTree(response.folders || []);
    } catch (error) {
      console.error("Failed to load folder hierarchy:", error);
      showToast("Failed to load folder hierarchy", "error");
    } finally {
      setLoadingFolders(false);
    }
  };

  const toggleFolder = (folderName) => {
    setExpandedFolders((prev) => ({ ...prev, [folderName]: !prev[folderName] }));
  };

  const handleZipUpload = async () => {
    if (!zipRepository.trim()) {
      showToast("Please enter a repository name", "warning");
      return;
    }
    if (!zipFile) {
      showToast("Please select a .zip file", "warning");
      return;
    }
    setUploadingZip(true);
    try {
      const response = await ApiClient.uploadZipRepository(zipRepository.trim(), zipFile);
      showToast(response.message || `Repository '${zipRepository}' uploaded successfully.`, "success");
      setZipFile(null);
      if (zipInputRef.current) zipInputRef.current.value = "";
      (response.files || []).forEach((file) => pollDocumentStatus(file.id));
      await loadDocuments();
    } catch (error) {
      console.error("Zip upload failed:", error);
      showToast(error.message || "Zip upload failed. Please try again.", "error");
    } finally {
      setUploadingZip(false);
    }
  };

  const handleFiles = async (files) => {
    if (files.length === 0) return;
    setUploading(true);
    try {
      const response = await ApiClient.uploadDocuments(files, {});
      const uploadedFiles = response.files || [];
      const processingDocs = uploadedFiles.map((file) => ({
        name: file.filename,
        chunks: "Processing...",
        status: "processing",
        dbId: file.id,
      }));
      setDocuments((prev) => [...prev, ...processingDocs]);
      showToast(`Uploaded ${files.length} document(s).`, "success");
      uploadedFiles.forEach((file) => {
        pollDocumentStatus(file.id);
      });
    } catch (error) {
      console.error("Upload failed:", error);
      showToast("Upload failed. Please try again.", "error");
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const pollDocumentStatus = async (docId) => {
    const checkStatus = async () => {
      try {
        const statusResponse = await ApiClient.getDocumentStatus(docId);
        if (statusResponse.status === "completed") {
          await loadDocuments();
        } else if (statusResponse.status === "failed") {
          await loadDocuments();
          const detail = statusResponse.status_detail || "";
          if (detail.includes("No embedder configured")) {
            showToast("⚠️ No embedder configured. Please configure an embedder.", "error", 8000);
          }
        } else {
          setTimeout(checkStatus, 5000);
        }
      } catch (error) {
        console.error("Status polling failed:", error);
        setTimeout(checkStatus, 1000);
      }
    };
    setTimeout(checkStatus, 100);
  };

  const removeDocument = (docId) => {
    if (!docId) {
      showToast("Cannot delete document: Invalid document ID", "error");
      return;
    }
    setDocToDelete(docId);
    setIsDeleteModalOpen(true);
  };

  const confirmDelete = async () => {
    if (!docToDelete) return;
    setIsDeleting(true);
    try {
      await ApiClient.deleteDocument(docToDelete);
      await loadDocuments();
      showToast("Document deleted successfully", "success");
      setIsDeleteModalOpen(false);
    } catch (error) {
      console.error("Delete failed:", error);
      showToast("Delete failed. Please try again.", "error");
    } finally {
      setIsDeleting(false);
    }
  };

  const handleDownloadDocument = async (docId, filename) => {
    try {
      await ApiClient.downloadDocument(docId, filename);
      showToast("Download started successfully!", "success");
    } catch (error) {
      console.error("Download failed:", error);
      showToast("Download failed. Please try again.", "error");
    }
  };

  const handleViewChunks = async (doc) => {
    setIsChunksDrawerOpen(true);
    setLoadingChunks(true);
    setSelectedDocForChunks(null);
    try {
      const response = await ApiClient.getDocumentChunks(doc.dbId);
      if (response && response.document) {
        setSelectedDocForChunks({
          ...response.document,
          name: response.document.original_name,
          dbId: response.document.id
        });
      }
      setChunks(response.chunks || []);
    } catch (error) {
      console.error("Failed to load chunks:", error);
      showToast("Failed to retrieve document chunks", "error");
      setIsChunksDrawerOpen(false);
    } finally {
      setLoadingChunks(false);
    }
  };

  const handleWebsiteScrape = async () => {
    if (!websiteUrl.trim()) {
      showToast("Please enter a website URL", "warning");
      return;
    }
    setScraping(true);
    try {
      const response = await ApiClient.request("/documents/add-website", {
        method: "POST",
        body: JSON.stringify({
          url: websiteUrl,
          max_depth: 2,
          max_links: 10,
        }),
      });
      if (response.success !== false) {
        showToast(response.message || "Website content added successfully!", "success");
        setTimeout(() => {
          setWebsiteUrl("");
          setScraping(false);
          loadDocuments();
        }, 2000);
      } else {
        setScraping(false);
        throw new Error(response.message || "Failed to add website content");
      }
    } catch (error) {
      setScraping(false);
      console.error("Website scraping failed:", error);
      showToast("Failed to add website content", "error");
    }
  };

  const currentStrategy = strategies.find(s => s.id === vectorConfig.strategy) || strategies[0];

  return (
    <div className="min-h-screen bg-[#FDFCFD] pt-4 pb-8 px-8 lg:pt-6 lg:pb-12 lg:px-12 animate-fadeIn flex justify-center items-start border-t border-gray-100">
      <div className="w-full max-w-6xl space-y-12">
        <PageHeader
          title="Neural Repository"
          description="Infuse your agent with specialized knowledge. Upload, manage, and configure your proprietary datasets."
          actions={
            <div className="flex items-center gap-3 bg-white px-5 py-3 rounded-[24px] border border-gray-100 shadow-xl shadow-gray-100/50">
              <span className={`text-[10px] font-black uppercase tracking-[0.2em] ${vectorConfig.knowledge_source !== 'graph' ? "text-[#83bbe1]" : "text-gray-300"}`}>
                {vectorConfig.knowledge_source !== 'graph' ? "Active" : "Inactive"}
              </span>

              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={vectorConfig.knowledge_source !== 'graph'}
                  onChange={async () => {
                    const newSource = vectorConfig.knowledge_source === 'graph' ? 'vector' : 'graph';
                    const updated = { ...vectorConfig, knowledge_source: newSource };
                    setVectorConfig(updated);

                    setConfigSaving(true);
                    try {
                      await ApiClient.request('/api/vector/config', {
                        method: 'POST',
                        body: JSON.stringify(updated)
                      });
                      showToast(`Knowledge source updated to ${newSource === 'graph' ? 'Knowledge Graph' : 'Vector Search'}!`, "success");
                    } catch (e) {
                      showToast("Failed to update knowledge source", "error");
                    } finally {
                      setConfigSaving(false);
                    }
                  }}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-white border-2 border-gray-200 rounded-full peer peer-checked:border-[#83bbe1] transition-all duration-300 after:content-[''] after:absolute after:top-[4px] after:left-[4px] after:bg-gray-300 after:rounded-full after:h-4 after:w-4 after:transition-all duration-300 peer-checked:after:translate-x-5 peer-checked:after:bg-[#83bbe1]"></div>
              </label>
            </div>
          }
        />

        {/* Tab Selection */}
        <div className="flex justify-center p-1.5 gap-2 bg-white/50 backdrop-blur-md rounded-2xl mb-8 w-fit mx-auto shadow-sm border border-gray-100/50">
          <button
            onClick={() => setActiveTab("repository")}
            className={`px-8 py-2.5 rounded-xl font-bold text-[10px] uppercase tracking-widest transition-all flex items-center justify-center gap-2 ${activeTab === "repository"
              ? "bg-gray-900 text-white shadow-lg shadow-gray-200"
              : "text-gray-400 hover:text-gray-900 hover:bg-white"
              }`}
          >
            <Database size={14} />
            Repository
          </button>
          <button
            onClick={() => setActiveTab("folders")}
            className={`px-8 py-2.5 rounded-xl font-bold text-[10px] uppercase tracking-widest transition-all flex items-center justify-center gap-2 ${activeTab === "folders"
              ? "bg-gray-900 text-white shadow-lg shadow-gray-200"
              : "text-gray-400 hover:text-gray-900 hover:bg-white"
              }`}
          >
            <FolderTree size={14} />
            Folders
          </button>
          <button
            onClick={() => setActiveTab("settings")}
            className={`px-8 py-2.5 rounded-xl font-bold text-[10px] uppercase tracking-widest transition-all flex items-center justify-center gap-2 ${activeTab === "settings"
              ? "bg-gray-900 text-white shadow-lg shadow-gray-200"
              : "text-gray-400 hover:text-gray-900 hover:bg-white"
              }`}
          >
            <Settings size={14} />
            Vector Settings
          </button>
        </div>

        {activeTab === "repository" ? (
          <div className="grid gap-8 animate-slideIn">
            {/* Upload Area */}
            <div
              className={`bg-white rounded-[32px] p-8 border-2 border-dashed transition-all duration-300 relative overflow-hidden group ${isDragging
                ? "border-gray-900 bg-gray-50 ring-4 ring-gray-900/5"
                : "border-gray-100 bg-white hover:border-gray-300"
                } cursor-pointer shadow-xl shadow-gray-100/50`}
              onDragEnter={(e) => { e.preventDefault(); setIsDragging(true); }}
              onDragOver={(e) => e.preventDefault()}
              onDragLeave={() => setIsDragging(false)}
              onDrop={(e) => { e.preventDefault(); setIsDragging(false); handleFiles(Array.from(e.dataTransfer.files)); }}
              onClick={() => fileInputRef.current?.click()}
            >
              <input
                ref={fileInputRef}
                type="file"
                multiple
                onChange={(e) => handleFiles(Array.from(e.target.files))}
                className="hidden"
                accept=".pdf,.doc,.docx,.txt,.csv,.xlsx,.xls"
              />
              <div className="flex flex-col items-center justify-center py-6 text-center">
                <div className="w-16 h-16 bg-gray-50 rounded-[20px] border border-gray-100 flex items-center justify-center mb-5 group-hover:scale-105 transition-transform duration-300 shadow-sm">
                  <Upload className="w-8 h-8 text-gray-400 group-hover:text-gray-900 transition-colors" />
                </div>
                <h3 className="text-xl font-black text-gray-900 mb-1.5 tracking-tight uppercase">
                  {isDragging ? "Drop to Ingest" : "Neural Ingestion"}
                </h3>
                <p className="text-gray-400 text-[10px] font-bold uppercase tracking-widest mb-1 max-w-xs leading-relaxed">
                  Drag and drop files or click to browse
                </p>
                <div className="flex flex-wrap justify-center gap-1.5 mt-4">
                  {['PDF', 'DOCX', 'TXT', 'CSV', 'XLSX'].map(ext => (
                    <span key={ext} className="px-3 py-1 bg-gray-100 text-gray-400 rounded-lg text-[9px] font-bold tracking-wider">{ext}</span>
                  ))}
                </div>

                {uploading && (
                  <div className="mt-6 flex items-center gap-2 px-5 py-2 bg-gray-900 text-white rounded-xl shadow-xl animate-bounce">
                    <div className="w-3 h-3 border-2 border-white/20 border-t-white rounded-full animate-spin"></div>
                    <span className="font-bold text-[9px] uppercase tracking-widest">Cognitive Processing...</span>
                  </div>
                )}
              </div>
            </div>

            {/* Zip Repository Upload */}
            <div className="bg-white rounded-[28px] p-6 border border-gray-100 shadow-xl shadow-gray-100/50">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 flex-shrink-0 flex items-center justify-center">
                  <img src={uploadHierarch} alt="" className="w-full h-full object-contain" />
                </div>
                <div>
                  <h3 className="font-bold text-gray-900 text-xs uppercase tracking-widest tracking-tight">Repository Upload (.zip)</h3>
                  <p className="text-[9px] text-gray-400 uppercase tracking-widest font-bold">Folder-structure-aware ingestion</p>
                </div>
              </div>
              <div className="grid md:grid-cols-[1fr_1fr_auto] gap-3 items-stretch">
                <input
                  type="text"
                  placeholder="Repository name (e.g. xshield-doc)"
                  value={zipRepository}
                  onChange={(e) => setZipRepository(e.target.value)}
                  className="w-full px-5 py-3 bg-gray-50 border-0 rounded-xl focus:ring-2 focus:ring-gray-900 text-gray-900 text-[11px] transition-all"
                />
                <button
                  type="button"
                  onClick={() => zipInputRef.current?.click()}
                  className="w-full px-5 py-3 bg-gray-50 border-0 rounded-xl text-left text-[11px] font-bold text-gray-500 hover:text-gray-900 transition-all truncate"
                >
                  {zipFile ? zipFile.name : "Choose .zip file..."}
                </button>
                <input
                  ref={zipInputRef}
                  type="file"
                  accept=".zip"
                  onChange={(e) => setZipFile(e.target.files[0] || null)}
                  className="hidden"
                />
                <button
                  onClick={handleZipUpload}
                  disabled={uploadingZip || !zipRepository.trim() || !zipFile}
                  className="px-8 py-3 bg-gray-900 text-white rounded-xl hover:bg-gray-800 disabled:opacity-50 transition-all font-bold text-[10px] uppercase tracking-widest shadow-lg shadow-gray-100 whitespace-nowrap"
                >
                  {uploadingZip ? "Extracting..." : "Upload Repository"}
                </button>
              </div>
              <p className="text-[9px] text-gray-300 font-bold uppercase tracking-widest mt-4">
                Preserves folder hierarchy &middot; tags chunks with repository/folder metadata &middot; view structure in the Folders tab
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-8">
              {/* Firecrawl Configuration */}
              <div className="bg-white rounded-[28px] p-6 border border-gray-100 shadow-xl shadow-gray-100/50">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 bg-gray-900 rounded-xl flex items-center justify-center text-white">
                    <ShieldCheck size={20} />
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-900 text-xs uppercase tracking-widest tracking-tight">Crawler Auth</h3>
                    <p className="text-[9px] text-gray-400 uppercase tracking-widest font-bold">Secure parsing Engine</p>
                  </div>
                </div>
                <div className="space-y-3">
                  <div className="relative">
                    <input
                      type={showFirecrawlKey ? "text" : "password"}
                      placeholder="Firecrawl API Key"
                      value={firecrawlApiKey}
                      onChange={(e) => setFirecrawlApiKey(e.target.value)}
                      className="w-full px-5 py-3 pr-12 bg-gray-50 border-0 rounded-xl focus:ring-2 focus:ring-gray-900 text-gray-900 font-mono text-[11px] transition-all"
                    />
                    <button
                      type="button"
                      onClick={() => setShowFirecrawlKey(p => !p)}
                      className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      {showFirecrawlKey ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                  </div>
                  <button
                    onClick={saveFirecrawlConfig}
                    disabled={savingFirecrawl || !firecrawlApiKey.trim()}
                    className="w-full py-3.5 bg-gray-900 text-white rounded-xl hover:bg-gray-800 disabled:opacity-50 transition-all font-bold text-[10px] uppercase tracking-widest shadow-lg shadow-gray-100"
                  >
                    {savingFirecrawl ? "Synchronizing..." : "Connect Neural Crawler"}
                  </button>
                </div>
              </div>

              {/* Website Scraping */}
              <div className="bg-white rounded-[28px] p-6 border border-gray-100 shadow-xl shadow-gray-100/50">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 bg-blue-50 rounded-xl flex items-center justify-center text-blue-600">
                    <Globe size={20} />
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-900 text-xs uppercase tracking-widest tracking-tight">URL Ingestion</h3>
                    <p className="text-[9px] text-gray-400 uppercase tracking-widest font-bold">Live web synchronization</p>
                  </div>
                </div>
                <div className="space-y-3">
                  <input
                    type="url"
                    placeholder="https://docs.example.com"
                    value={websiteUrl}
                    onChange={(e) => setWebsiteUrl(e.target.value)}
                    disabled={!firecrawlConfigured || scraping}
                    className="w-full px-5 py-3 bg-gray-50 border-0 rounded-xl focus:ring-2 focus:ring-blue-500 text-gray-900 text-[11px] transition-all disabled:opacity-50"
                  />
                  <button
                    onClick={handleWebsiteScrape}
                    disabled={scraping || !websiteUrl.trim() || !firecrawlConfigured}
                    className="w-full py-3.5 bg-gray-900 text-white rounded-xl hover:bg-gray-800 disabled:opacity-50 transition-all font-bold text-[10px] uppercase tracking-widest shadow-xl shadow-gray-100 flex items-center justify-center gap-2"
                  >
                    {scraping ? "Crawl Active..." : "Start Neural Scrape"}
                  </button>
                </div>
              </div>
            </div>



            {/* List */}
            {documents.length > 0 && (
              <div className="bg-white rounded-2xl p-6 border border-gray-100 shadow-sm">
                <div className="flex items-center justify-between mb-6 px-1">
                  <h3 className="font-bold text-gray-900 text-xs uppercase tracking-widest">Cognitive Repository</h3>
                  <span className="bg-gray-100 text-gray-400 px-3 py-1 rounded-md text-[8px] font-black uppercase tracking-widest">
                    {documents.length} Vectors Total
                  </span>
                </div>
                <div className="grid gap-2 mb-4 max-h-[350px] overflow-y-auto pr-2 custom-scrollbar">
                  {documents.slice((currentPage - 1) * DOCS_PER_PAGE, currentPage * DOCS_PER_PAGE).map((doc, index) => (
                    <div
                      key={doc.id || index}
                      className="flex items-center justify-between p-2.5 bg-gray-50/50 rounded-xl border border-gray-100 hover:border-gray-200 transition-all duration-300 group"
                    >
                      <div className="flex items-center gap-3 flex-1 min-w-0">
                        <div className="w-9 h-9 bg-white rounded-lg flex items-center justify-center text-gray-400 group-hover:text-gray-900 transition-colors shadow-sm">
                          <FileText size={18} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-[11px] font-black text-gray-900 truncate pr-4 tracking-tight uppercase">
                            {doc.name}
                          </p>
                          <div className="flex items-center gap-2 mt-0.5">
                            {doc.status === "processing" ? (
                              <span className="flex items-center gap-1.5 text-[8px] font-black text-gray-500 bg-gray-50 px-1.5 py-0.5 rounded uppercase tracking-wider">
                                <RefreshCcw size={8} className="animate-spin" /> Processing
                              </span>
                            ) : doc.status === "failed" ? (
                              <span className="text-[8px] font-black text-red-500 bg-red-50 px-1.5 py-0.5 rounded uppercase tracking-wider">
                                Error: {doc.status_detail || "Neural Failure"}
                              </span>
                            ) : (
                              <span className="flex items-center gap-1.5 text-[8px] font-black text-blue-600 bg-blue-50 px-1.5 py-0.5 rounded uppercase tracking-wider">
                                <CheckCircle size={8} /> {doc.chunks || 0} Chunks
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => handleViewChunks(doc)}
                          className="p-2 text-gray-300 hover:text-gray-900 hover:bg-gray-50 rounded-lg transition-all"
                          title="View chunks"
                        >
                          <Eye size={16} />
                        </button>
                        <button
                          onClick={() => handleDownloadDocument(doc.dbId, doc.name)}
                          className="p-2 text-gray-300 hover:text-blue-500 hover:bg-blue-50 rounded-lg transition-all"
                          title="Download document"
                        >
                          <Download size={16} />
                        </button>
                        <button
                          onClick={() => removeDocument(doc.dbId)}
                          className="p-2 text-gray-300 hover:text-red-500 hover:bg-red-50 rounded-lg transition-all"
                          title="Delete document"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Pagination Controls */}
                {Math.ceil(documents.length / DOCS_PER_PAGE) > 1 && (
                  <div className="flex items-center justify-between pt-4 border-t border-gray-50">
                    <span className="text-[9px] font-bold text-gray-400 uppercase tracking-widest px-2">
                      Page {currentPage} of {Math.ceil(documents.length / DOCS_PER_PAGE)}
                    </span>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                        disabled={currentPage === 1}
                        className="p-1.5 rounded-lg border border-gray-100 text-gray-400 hover:text-gray-900 hover:bg-gray-50 disabled:opacity-30 disabled:hover:bg-transparent transition-all"
                      >
                        <ChevronLeft size={14} />
                      </button>
                      <button
                        onClick={() => setCurrentPage(prev => Math.min(prev + 1, Math.ceil(documents.length / DOCS_PER_PAGE)))}
                        disabled={currentPage === Math.ceil(documents.length / DOCS_PER_PAGE)}
                        className="p-1.5 rounded-lg border border-gray-100 text-gray-400 hover:text-gray-900 hover:bg-gray-50 disabled:opacity-30 disabled:hover:bg-transparent transition-all"
                      >
                        <ChevronRight size={14} />
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        ) : activeTab === "folders" ? (
          /* Folder Hierarchy Tab Content */
          <div className="grid gap-8 animate-slideIn">
            <div className="bg-white rounded-[28px] p-6 border border-gray-100 shadow-xl shadow-gray-100/50">
              <div className="flex items-center justify-between mb-6 flex-wrap gap-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl overflow-hidden flex-shrink-0">
                    <img src={folderHierarch} alt="" className="w-full h-full object-cover" />
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-900 text-xs uppercase tracking-widest">Repository Hierarchy</h3>
                    <p className="text-[9px] text-gray-400 uppercase tracking-widest font-bold">Folder-aware knowledge structure</p>
                  </div>
                </div>
                <Dropdown
                  options={[
                    { id: "", name: "All Repositories" },
                    ...repositories.map((repo) => ({ id: repo, name: repo }))
                  ]}
                  value={selectedRepository}
                  onChange={(val) => setSelectedRepository(val)}
                  className="w-full md:w-60"
                  buttonClassName="px-4 py-2.5 border border-gray-100 rounded-xl hover:border-gray-900 shadow-sm"
                />
              </div>

              {loadingFolders ? (
                <div className="flex items-center justify-center py-12 text-gray-400">
                  <RefreshCcw size={20} className="animate-spin" />
                </div>
              ) : folderTree.length === 0 ? (
                <div className="text-center py-12">
                  <Folder size={32} className="text-gray-200 mx-auto mb-3" />
                  <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">No folder-uploaded documents yet</p>
                  <p className="text-[9px] text-gray-300 mt-1">Upload a repository (.zip) from the Repository tab to see its structure here.</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {folderTree.map((folder) => (
                    <div key={folder.name} className="border border-gray-100 rounded-2xl overflow-hidden">
                      <button
                        onClick={() => toggleFolder(folder.name)}
                        className="w-full flex items-center justify-between p-3.5 bg-gray-50/50 hover:bg-gray-50 transition-colors"
                      >
                        <div className="flex items-center gap-2.5">
                          {expandedFolders[folder.name] ? (
                            <ChevronDown size={14} className="text-gray-400" />
                          ) : (
                            <ChevronRight size={14} className="text-gray-400" />
                          )}
                          <Folder size={16} className="text-gray-400" />
                          <span className="text-[11px] font-black text-gray-900 tracking-tight">{folder.name || "(root)"}</span>
                        </div>
                        <span className="bg-gray-100 text-gray-400 px-2.5 py-1 rounded-md text-[8px] font-black uppercase tracking-widest">
                          {folder.files.length} file{folder.files.length !== 1 ? "s" : ""}
                        </span>
                      </button>
                      {expandedFolders[folder.name] && (
                        <div className="p-2 space-y-1.5 bg-white">
                          {folder.files.map((file, idx) => (
                            <div key={idx} className="flex items-center justify-between p-2.5 pl-8 bg-gray-50/30 rounded-xl">
                              <div className="flex items-center gap-2.5 min-w-0">
                                <FileText size={14} className="text-gray-300 shrink-0" />
                                <span className="text-[10px] font-bold text-gray-700 truncate">{file.name}</span>
                              </div>
                              {file.status === "processing" ? (
                                <span className="flex items-center gap-1.5 text-[8px] font-black text-gray-500 bg-gray-50 px-1.5 py-0.5 rounded uppercase tracking-wider shrink-0">
                                  <RefreshCcw size={8} className="animate-spin" /> Processing
                                </span>
                              ) : file.status === "failed" ? (
                                <span
                                  className="text-[8px] font-black text-red-500 bg-red-50 px-1.5 py-0.5 rounded uppercase tracking-wider shrink-0"
                                  title={file.status_detail}
                                >
                                  Error
                                </span>
                              ) : (
                                <span className="flex items-center gap-1.5 text-[8px] font-black text-blue-600 bg-blue-50 px-1.5 py-0.5 rounded uppercase tracking-wider shrink-0">
                                  <CheckCircle size={8} /> {file.chunks || 0} Chunks
                                </span>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        ) : (
          /* Settings Tab Content */
          <div className="animate-slideIn space-y-8">
            {/* 1. Chunking Strategy Card */}
            <div className="bg-white border border-gray-100 rounded-[40px] shadow-xl shadow-gray-100/50 relative">
              {/* Strategy Switcher (Inside Card Top) */}
              <div className="bg-gray-50/50 p-6 border-b border-gray-100">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 px-4">
                  <div className="flex items-center gap-3">
                    <img src={chunkinIcon} alt="Chunking" className="w-12 h-12 object-contain" />
                    <div>
                      <h3 className="font-bold text-gray-900 text-xs uppercase tracking-widest uppercase tracking-tight">Chunking Strategy</h3>
                      <p className="text-[10px] text-gray-400 font-bold uppercase tracking-widest">Select the neural partitioning method</p>
                    </div>
                  </div>

                  {/* Custom Dropdown + Info Tooltip */}
                  <div className="flex items-center gap-3 w-full md:w-auto">
                    <Dropdown
                      options={strategies}
                      value={vectorConfig.strategy}
                      onChange={(val) => setVectorConfig({ ...vectorConfig, strategy: val })}
                      className="w-full md:w-80"
                      buttonClassName="px-6 py-3.5 border border-gray-200 rounded-2xl hover:border-gray-900 shadow-sm"
                      renderSelected={(selected) => {
                        const SelectedIcon = selected?.icon || Layers;
                        return (
                          <div className="flex items-center gap-3">
                            <SelectedIcon size={18} className="text-gray-900" />
                            <span className="text-[11px] font-black uppercase tracking-widest">{selected?.name}</span>
                          </div>
                        );
                      }}
                      renderOption={(s, isSelected) => (
                        <div className="flex items-center gap-4 w-full">
                          <div className={`p-2 rounded-xl ${isSelected ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-400'}`}>
                            <s.icon size={16} />
                          </div>
                          <div>
                            <p className={`text-[11px] font-black uppercase tracking-widest ${isSelected ? 'text-gray-900' : 'text-gray-500'}`}>
                              {s.name}
                            </p>
                            <p className="text-[9px] text-gray-400 font-medium leading-tight mt-0.5">{s.description}</p>
                          </div>
                        </div>
                      )}
                    />

                    {/* Tooltip Wrapper */}
                    <div className="relative group flex items-center justify-center shrink-0">
                      <div className="w-8 h-8 rounded-full border border-gray-200 hover:border-gray-900 flex items-center justify-center text-gray-500 hover:text-gray-900 cursor-help transition-colors">
                        <Info size={16} />
                      </div>
                      {/* Tooltip Box */}
                      <div className="absolute right-[-8px] bottom-full mb-3 w-[230px] p-3 bg-gray-900 text-white text-[9.5px] font-bold uppercase tracking-wider leading-relaxed rounded-2xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-30 shadow-2xl pointer-events-none">
                        <div className="relative z-10">
                          Chunking strategies are immutable for existing documents (new uploads will inherit them). Retrieval and reranking parameters take effect instantly.
                        </div>
                        {/* Tooltip Arrow */}
                        <div className="absolute top-full right-[18px] -mt-1 w-3 h-3 bg-gray-900 rotate-45 z-0" />
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Main Params Area */}
              <div className="p-10 lg:p-14 space-y-8">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h4 className="text-xl font-bold text-gray-900 tracking-tight uppercase">{currentStrategy.name}</h4>
                    <p className="text-gray-400 font-semibold text-[11px] leading-none mt-1 max-w-md">{currentStrategy.description}</p>
                  </div>
                  <button
                    onClick={saveVectorConfig}
                    disabled={configSaving}
                    className="flex items-center gap-2 px-10 py-3 bg-gray-900 text-white rounded-xl font-bold text-[10px] uppercase tracking-widest hover:bg-gray-800 transition-all shadow-xl shadow-gray-200 active:scale-95 disabled:opacity-40"
                  >
                    {configSaving ? <RefreshCcw className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                    {configSaving ? 'Propagating...' : 'Deploy Configuration'}
                  </button>
                </div>

                <div className="grid grid-cols-1 gap-12 pt-8">
                  {/* Chunk Size */}
                  <div className="space-y-6">
                    <div className="flex justify-between items-center">
                      <label className="text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] flex items-center gap-2">
                        Chunk Size <Info size={14} className="text-gray-300" />
                      </label>
                      <span className="px-4 py-1.5 bg-gray-900 text-white text-[10px] font-black rounded-full tracking-tight">
                        {vectorConfig.chunk_size} chars
                      </span>
                    </div>
                    <input
                      type="range" min="200" max="8000" step="100"
                      value={vectorConfig.chunk_size}
                      onChange={(e) => setVectorConfig({ ...vectorConfig, chunk_size: parseInt(e.target.value) })}
                      className="w-full h-1.5 bg-gray-100 rounded-lg appearance-none cursor-pointer accent-gray-900"
                    />
                  </div>

                  {/* Overlap / Threshold Row */}
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-12">
                    {currentStrategy.params.includes('chunk_overlap') && (
                      <div className="space-y-6">
                        <div className="flex justify-between items-center">
                          <label className="text-[10px] font-black text-gray-400 uppercase tracking-[0.2em]">Chunk Overlap</label>
                          <span className="px-4 py-1.5 bg-gray-50 text-gray-900 text-[10px] font-black rounded-full tracking-tight border border-gray-100">
                            {vectorConfig.chunk_overlap} chars
                          </span>
                        </div>
                        <input
                          type="range" min="0" max="1000" step="50"
                          value={vectorConfig.chunk_overlap}
                          onChange={(e) => setVectorConfig({ ...vectorConfig, chunk_overlap: parseInt(e.target.value) })}
                          className="w-full h-1.5 bg-gray-100 rounded-lg appearance-none cursor-pointer accent-gray-900"
                        />
                      </div>
                    )}

                    <div className="space-y-6">
                      <div className="flex justify-between items-center">
                        <label className="text-[10px] font-black text-gray-400 uppercase tracking-[0.2em]">
                          {vectorConfig.params?.reranker_type && vectorConfig.params?.reranker_type !== "none"
                            ? "Database Retrieval Pool (K)"
                            : "Retrieval Context (K)"}
                        </label>
                        <span className="px-4 py-1.5 bg-gray-50 text-gray-900 text-[10px] font-black rounded-full tracking-tight border border-gray-100">
                          {vectorConfig.search_top_k} Results
                        </span>
                      </div>
                      <input
                        type="range"
                        min="1"
                        max={vectorConfig.params?.reranker_type && vectorConfig.params?.reranker_type !== "none" ? "50" : "20"}
                        step="1"
                        value={vectorConfig.search_top_k}
                        onChange={(e) => setVectorConfig({ ...vectorConfig, search_top_k: parseInt(e.target.value) })}
                        className="w-full h-1.5 bg-gray-100 rounded-lg appearance-none cursor-pointer accent-gray-900"
                      />
                    </div>

                    <div className="space-y-4">
                      <label className="text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] block">
                        Querying Type
                      </label>
                      <Dropdown
                        options={[
                          { value: "hybrid", label: "Hybrid Search" },
                          { value: "vector", label: "Vector Search" },
                          { value: "keyword", label: "Keyword Search" }
                        ]}
                        value={vectorConfig.search_type || "hybrid"}
                        onChange={(val) => setVectorConfig({ ...vectorConfig, search_type: val })}
                        buttonClassName="w-full border border-gray-200 rounded-2xl hover:border-gray-900 px-5 py-3 text-[11px] font-black uppercase tracking-widest text-left"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* 2. Multimodal RAG Card */}
            <div className="bg-white border border-gray-100 rounded-[40px] p-10 lg:p-14 shadow-xl shadow-gray-100/50 space-y-6">
              <div className="flex items-center justify-between flex-wrap gap-4">
                <div className="flex items-center gap-3">
                  <img src={multimodalIcon} alt="Multimodal RAG" className="w-12 h-12 object-contain" />
                  <div>
                    <h3 className="font-bold text-gray-900 text-xs uppercase tracking-widest uppercase tracking-tight">Multimodal RAG</h3>
                    <p className="text-[10px] text-gray-400 font-bold uppercase tracking-widest">Enable image processing & vision descriptions for PDF documents</p>
                  </div>
                </div>

                <button
                  type="button"
                  onClick={async () => {
                    const isEnabled = vectorConfig.params?.multimodal_rag || false;
                    const updatedConfig = {
                      ...vectorConfig,
                      params: {
                        ...vectorConfig.params,
                        multimodal_rag: !isEnabled
                      }
                    };
                    setVectorConfig(updatedConfig);
                    // Automatically save to backend
                    setConfigSaving(true);
                    try {
                      await ApiClient.request('/api/vector/config', {
                        method: 'POST',
                        body: JSON.stringify(updatedConfig)
                      });
                      showToast(`Multimodal RAG ${!isEnabled ? 'enabled' : 'disabled'} successfully!`, "success");
                    } catch (error) {
                      console.error('Error saving vector config:', error);
                      showToast("Failed to save neural parameters", "error");
                    } finally {
                      setConfigSaving(false);
                    }
                  }}
                  className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${vectorConfig.params?.multimodal_rag ? 'bg-gray-900' : 'bg-gray-200'
                    }`}
                >
                  <span
                    className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${vectorConfig.params?.multimodal_rag ? 'translate-x-5' : 'translate-x-0'
                      }`}
                  />
                </button>
              </div>
              <p className="text-[10px] text-gray-400 font-semibold leading-normal max-w-2xl">
                Enabling this will extract images, call the configured Vision Model to describe them, and inline URLs/descriptions for vectorization.
              </p>
            </div>

            {/* 3. Reranking Engine Card */}
            <div className="bg-white border border-gray-100 rounded-[40px] p-10 lg:p-14 shadow-xl shadow-gray-100/50 space-y-6">
              <div className="flex items-center gap-3">
                <img src={rerankinIcon} alt="Reranking" className="w-12 h-12 object-contain" />
                <div>
                  <h3 className="font-bold text-gray-900 text-xs uppercase tracking-widest uppercase tracking-tight">Reranking Engine</h3>
                  <p className="text-[10px] text-gray-400 font-bold uppercase tracking-widest">Optimize search relevance using a semantic reranker model</p>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 items-end">
                <div className="space-y-2">
                  <label className="text-[9px] font-bold text-gray-400 uppercase tracking-wider block">Select Reranker</label>
                  <Dropdown
                    options={[
                      { value: "none", label: "None (Standard Search)" },
                      { value: "cohere", label: "Cohere Rerank" },
                      { value: "aws_bedrock", label: "AWS Bedrock Rerank" }
                    ]}
                    value={vectorConfig.params?.reranker_type || "none"}
                    onChange={(val) => setVectorConfig({
                      ...vectorConfig,
                      params: { ...vectorConfig.params, reranker_type: val }
                    })}
                    buttonClassName="border border-gray-200 rounded-2xl hover:border-gray-900"
                  />
                </div>

                {(vectorConfig.params?.reranker_type === "cohere") && (
                  <div className="space-y-2">
                    <label className="text-[9px] font-bold text-gray-400 uppercase tracking-wider block">Cohere Model</label>
                    <Dropdown
                      options={[
                        { value: "rerank-multilingual-v3.0", label: "Rerank Multilingual v3.0" },
                        { value: "rerank-english-v3.0", label: "Rerank English v3.0" },
                        { value: "rerank-multilingual-v2.0", label: "Rerank Multilingual v2.0" },
                        { value: "rerank-english-v2.0", label: "Rerank English v2.0" }
                      ]}
                      value={vectorConfig.params?.reranker_model || "rerank-multilingual-v3.0"}
                      onChange={(val) => setVectorConfig({
                        ...vectorConfig,
                        params: { ...vectorConfig.params, reranker_model: val }
                      })}
                      buttonClassName="border border-gray-200 rounded-2xl hover:border-gray-900"
                    />
                  </div>
                )}

                {(vectorConfig.params?.reranker_type === "cohere") && (
                  <div className="space-y-2">
                    <label className="text-[9px] font-bold text-gray-400 uppercase tracking-wider block">Cohere API Key</label>
                    <input
                      type="password"
                      placeholder="Enter Cohere API Key"
                      value={vectorConfig.params?.reranker_api_key || ""}
                      onChange={(e) => {
                        setVectorConfig({
                          ...vectorConfig,
                          params: {
                            ...vectorConfig.params,
                            reranker_api_key: e.target.value
                          }
                        });
                      }}
                      className="w-full px-5 py-3.5 bg-gray-50 border border-gray-200 rounded-2xl focus:ring-2 focus:ring-gray-900 focus:border-gray-900 text-gray-900 text-[11px]"
                    />
                  </div>
                )}

                {(vectorConfig.params?.reranker_type === "aws_bedrock") && (
                  <>
                    <div className="space-y-2">
                      <label className="text-[9px] font-bold text-gray-400 uppercase tracking-wider block">AWS Bedrock Model</label>
                      <Dropdown
                        options={[
                          { value: "cohere.rerank-v3-5:0", label: "Cohere Rerank 3.5" },
                          { value: "amazon.rerank-v1:0", label: "Amazon Rerank 1.0" }
                        ]}
                        value={vectorConfig.params?.aws_bedrock_model || "cohere.rerank-v3-5:0"}
                        onChange={(val) => setVectorConfig({
                          ...vectorConfig,
                          params: { ...vectorConfig.params, aws_bedrock_model: val }
                        })}
                        buttonClassName="border border-gray-200 rounded-2xl hover:border-gray-900"
                      />
                    </div>

                    <div className="space-y-2">
                      <label className="text-[9px] font-bold text-gray-400 uppercase tracking-wider block">AWS Access Key ID</label>
                      <input
                        type="text"
                        placeholder="Enter AWS Access Key ID"
                        value={vectorConfig.params?.aws_access_key_id || ""}
                        onChange={(e) => {
                          setVectorConfig({
                            ...vectorConfig,
                            params: {
                              ...vectorConfig.params,
                              aws_access_key_id: e.target.value
                            }
                          });
                        }}
                        className="w-full px-5 py-3.5 bg-gray-50 border border-gray-200 rounded-2xl focus:ring-2 focus:ring-gray-900 focus:border-gray-900 text-gray-900 text-[11px]"
                      />
                    </div>

                    <div className="space-y-2">
                      <label className="text-[9px] font-bold text-gray-400 uppercase tracking-wider block">AWS Secret Access Key</label>
                      <input
                        type="password"
                        placeholder="Enter AWS Secret Access Key"
                        value={vectorConfig.params?.aws_secret_access_key || ""}
                        onChange={(e) => {
                          setVectorConfig({
                            ...vectorConfig,
                            params: {
                              ...vectorConfig.params,
                              aws_secret_access_key: e.target.value
                            }
                          });
                        }}
                        className="w-full px-5 py-3.5 bg-gray-50 border border-gray-200 rounded-2xl focus:ring-2 focus:ring-gray-900 focus:border-gray-900 text-gray-900 text-[11px]"
                      />
                    </div>

                    <div className="space-y-2">
                      <label className="text-[9px] font-bold text-gray-400 uppercase tracking-wider block">AWS Region</label>
                      <input
                        type="text"
                        placeholder="Enter AWS region"
                        value={vectorConfig.params?.aws_region || ""}
                        onChange={(e) => {
                          setVectorConfig({
                            ...vectorConfig,
                            params: {
                              ...vectorConfig.params,
                              aws_region: e.target.value
                            }
                          });
                        }}
                        className="w-full px-5 py-3.5 bg-gray-50 border border-gray-200 rounded-2xl focus:ring-2 focus:ring-gray-900 focus:border-gray-900 text-gray-900 text-[11px]"
                      />
                    </div>
                  </>
                )}

                {vectorConfig.params?.reranker_type && vectorConfig.params?.reranker_type !== "none" && (
                  <div className="space-y-4 w-full">
                    <div className="flex justify-between items-center">
                      <label className="text-[9px] font-bold text-gray-400 uppercase tracking-wider block">Reranker Top-N Chunks</label>
                      <span className="px-3 py-1 bg-gray-50 text-gray-900 text-[10px] font-black rounded-full tracking-tight border border-gray-100">
                        {vectorConfig.params?.reranker_top_n || 5} Chunks
                      </span>
                    </div>
                    <input
                      type="range" min="1" max="20" step="1"
                      value={vectorConfig.params?.reranker_top_n || 5}
                      onChange={(e) => {
                        setVectorConfig({
                          ...vectorConfig,
                          params: {
                            ...vectorConfig.params,
                            reranker_top_n: parseInt(e.target.value)
                          }
                        });
                      }}
                      className="w-full h-1.5 bg-gray-100 rounded-lg appearance-none cursor-pointer accent-gray-900"
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      <DeleteConfirmModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={confirmDelete}
        isLoading={isDeleting}
        title="Delete Neural Data?"
        message="This action will permanently erase this document's cognitive signature from the neural memory."
      />

      {/* Chunks Side Drawer */}
      {isChunksDrawerOpen && (
        <div className="fixed inset-0 z-50 flex justify-end">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/30 backdrop-blur-sm transition-opacity duration-300"
            onClick={() => setIsChunksDrawerOpen(false)}
          />
          {/* Drawer Panel */}
          <div className="relative w-full max-w-3xl bg-white h-full shadow-2xl flex flex-col z-10 transition-transform duration-300 transform translate-x-0 border-l border-gray-100">
            {/* Header */}
            <div className="p-6 border-b border-gray-100 flex items-center justify-between bg-gray-50/50">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-gray-900 rounded-xl flex items-center justify-center text-white">
                  <Database size={20} />
                </div>
                <div>
                  <h3 className="font-bold text-gray-900 text-xs uppercase tracking-widest truncate max-w-md">
                    {selectedDocForChunks ? selectedDocForChunks.name : "Loading document..."}
                  </h3>
                  <p className="text-[9px] text-gray-400 uppercase tracking-widest font-bold">
                    Knowledge Base Chunk Viewer
                  </p>
                </div>
              </div>
              <button
                onClick={() => setIsChunksDrawerOpen(false)}
                className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 text-[10px] font-bold uppercase tracking-widest rounded-xl transition-all"
              >
                Close
              </button>
            </div>

            {/* Body */}
            <div className="flex-1 overflow-hidden grid grid-cols-1 md:grid-cols-[250px_1fr] divide-y md:divide-y-0 md:divide-x divide-gray-100">
              {/* Left Pane - Metadata */}
              <div className="p-6 bg-gray-50/30 overflow-y-auto space-y-6">
                <div>
                  <span className="text-[9px] font-bold text-gray-400 uppercase tracking-widest block mb-1">Document ID</span>
                  <span className="text-[10px] font-mono text-gray-700 break-all select-all bg-gray-100 px-2 py-1 rounded-md block">
                    {selectedDocForChunks ? selectedDocForChunks.document_id : "Loading..."}
                  </span>
                </div>
                <div>
                  <span className="text-[9px] font-bold text-gray-400 uppercase tracking-widest block mb-1">Created At</span>
                  <span className="text-[10px] font-bold text-gray-900 uppercase">
                    {(() => {
                      if (!selectedDocForChunks) return "Loading...";
                      const dt = selectedDocForChunks.created_at;
                      if (!dt) return "Unknown";
                      let clean = typeof dt === 'string' ? dt.replace(' ', 'T') : dt;
                      if (typeof clean === 'string' && !clean.endsWith('Z') && !clean.includes('+')) {
                        clean += 'Z';
                      }
                      const dateObj = new Date(clean);
                      return isNaN(dateObj.getTime()) ? String(dt) : dateObj.toLocaleString();
                    })()}
                  </span>
                </div>
                <div>
                  <span className="text-[9px] font-bold text-gray-400 uppercase tracking-widest block mb-1">File Size</span>
                  <span className="text-[10px] font-bold text-gray-900">
                    {selectedDocForChunks ? (selectedDocForChunks.file_size ? `${(selectedDocForChunks.file_size / 1024).toFixed(1)} KB` : 'Unknown') : 'Loading...'}
                  </span>
                </div>
                <div>
                  <span className="text-[9px] font-bold text-gray-400 uppercase tracking-widest block mb-1">Repository</span>
                  <span className="text-[10px] font-bold text-gray-900 uppercase">
                    {selectedDocForChunks ? (selectedDocForChunks.repository || "Global") : 'Loading...'}
                  </span>
                </div>
                <div>
                  <span className="text-[9px] font-bold text-gray-400 uppercase tracking-widest block mb-1">RAG Index Type</span>
                  <span className="text-[10px] font-bold text-gray-900 uppercase">
                    PgVector (Hybrid)
                  </span>
                </div>
                <div>
                  <span className="text-[9px] font-bold text-gray-400 uppercase tracking-widest block mb-1">Total Chunks</span>
                  <span className="px-2.5 py-1 bg-blue-50 text-blue-600 rounded-md text-[10px] font-black uppercase tracking-wider block w-fit">
                    {loadingChunks ? "Calculating..." : `${chunks.length} Chunks`}
                  </span>
                </div>
              </div>

              {/* Right Pane - Chunks Content */}
              <div className="flex-1 p-6 overflow-y-auto space-y-4 bg-gray-50/10">
                <h4 className="text-[10px] font-black text-gray-400 uppercase tracking-wider">File Content Slices</h4>
                {loadingChunks ? (
                  <div className="flex flex-col items-center justify-center h-64 text-gray-400">
                    <RefreshCcw size={24} className="animate-spin mb-3" />
                    <span className="text-[9px] font-bold uppercase tracking-widest">Loading Neural Chunks...</span>
                  </div>
                ) : chunks.length === 0 ? (
                  <div className="text-center py-20">
                    <AlertCircle className="w-8 h-8 text-gray-300 mx-auto mb-3" />
                    <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">No vectorized chunks found</p>
                    <p className="text-[9px] text-gray-300 mt-1">Check if the document status is fully completed.</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {chunks.map((chunk, index) => (
                      <div key={chunk.id || index} className="bg-white border border-gray-100 rounded-2xl p-5 shadow-sm space-y-3">
                        <div className="flex items-center justify-between">
                          <span className="bg-gray-100 text-gray-500 px-2 py-0.5 rounded text-[9px] font-black uppercase tracking-widest">
                            Chunk #{index + 1}
                          </span>
                          {chunk.meta_data?.page && (
                            <span className="text-[9px] font-bold text-gray-400 uppercase">
                              Page {chunk.meta_data.page}
                            </span>
                          )}
                        </div>
                        <p className="text-[12px] text-gray-700 leading-relaxed font-mono whitespace-pre-wrap select-text">
                          {chunk.text}
                        </p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DocumentUpload;
